import { computed, ref, toValue, watch } from 'vue';
import { getSportLiveTvCategoryEvents, searchSportLiveTvEvents } from '@/api/sport-live-tv';
import { SPORT_LIVE_TV_EVENTS_LIMIT, SPORT_LIVE_TV_SEARCH_LIMIT } from '@/sport-live-tv/constants';
import { arrangeSportLiveTvEvents, uniqueSportLiveTvEvents } from '@/sport-live-tv/helpers';

const fetchSingleLeaguePage = async (leagueId, loadedEvents) => {
  const page = await getSportLiveTvCategoryEvents(leagueId, loadedEvents.length, SPORT_LIVE_TV_EVENTS_LIMIT);

  return { events: [...loadedEvents, ...page], hasNextPage: page.length === SPORT_LIVE_TV_EVENTS_LIMIT };
};

const fetchSingleTeamPage = async (team, loadedEvents) => {
  const limit = loadedEvents.length + SPORT_LIVE_TV_SEARCH_LIMIT;
  const events = await searchSportLiveTvEvents(team, limit);

  return { events, hasNextPage: events.length === limit };
};

const fetchMultiSelection = async ({ leagues, teams }) => {
  const pages = teams.length
    ? await Promise.all(teams.map((team) => searchSportLiveTvEvents(team, SPORT_LIVE_TV_SEARCH_LIMIT)))
    : await Promise.all(leagues.map((leagueId) => getSportLiveTvCategoryEvents(leagueId, 0, SPORT_LIVE_TV_EVENTS_LIMIT)));
  const merged = uniqueSportLiveTvEvents(pages.flat());
  const events = leagues.length ? merged.filter(({ category_id }) => leagues.includes(category_id)) : merged;

  return { events, hasNextPage: false };
};

const fetchSelectionPage = (selection, loadedEvents) => {
  const { leagues, teams } = selection;

  if (leagues.length === 1 && !teams.length) return fetchSingleLeaguePage(leagues[0], loadedEvents);
  if (teams.length === 1 && !leagues.length) return fetchSingleTeamPage(teams[0], loadedEvents);

  return fetchMultiSelection(selection);
};

export const useSportLiveTvSelectionEvents = ({ selection, status, sort }) => {
  const loadedEvents = ref([]);
  const hasNextPage = ref(false);
  const isLoading = ref(false);
  const isFetchingNextPage = ref(false);
  const isError = ref(false);
  let requestId = 0;

  const isEmptySelection = (value) => !value.leagues.length && !value.teams.length;

  const loadPage = async (loadingState, previousEvents) => {
    const currentRequestId = ++requestId;
    loadingState.value = true;
    isError.value = false;

    try {
      const page = await fetchSelectionPage(toValue(selection), previousEvents);
      if (currentRequestId !== requestId) return;

      loadedEvents.value = page.events;
      hasNextPage.value = page.hasNextPage;
    } catch {
      if (currentRequestId === requestId) isError.value = true;
    } finally {
      if (currentRequestId === requestId) loadingState.value = false;
    }
  };

  const reload = () => {
    loadedEvents.value = [];
    hasNextPage.value = false;
    isFetchingNextPage.value = false;

    if (isEmptySelection(toValue(selection))) {
      requestId++;
      isLoading.value = false;
      return;
    }

    loadPage(isLoading, []);
  };

  const fetchNextPage = () => loadPage(isFetchingNextPage, loadedEvents.value);

  watch(() => toValue(selection), reload, { immediate: true });

  const events = computed(() => arrangeSportLiveTvEvents(loadedEvents.value, toValue(status), toValue(sort)));

  return { events, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, reload };
};
